import axios from 'axios';

// In production the SPA is served from /assist/app/, in dev (Vite) from /.
const APP_BASE = import.meta.env.PROD ? '/assist/app' : '';
export const SESSION_EXTENDED_EVENT = 'assist:session-extended';

const api = axios.create({
  baseURL: '/assist/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// URLs (relative to api.baseURL) that should not trigger the global
// session-expiry redirect on 401. The Login page handles its own errors.
const LOGIN_FAILURE_PATHS = [
  '/auth/j_spring_security_check',
  '/auth/session', // initial session probe; AuthContext handles 401 silently
];

const isLoginFailurePath = (url) => {
  if (!url) return false;
  return LOGIN_FAILURE_PATHS.some((path) => url.endsWith(path));
};

const SESSION_EXTENSION_EXCLUDED_PATHS = [
  '/auth/logout',
];

const shouldEmitSessionExtended = (url) => {
  if (!url) return false;
  return !SESSION_EXTENSION_EXCLUDED_PATHS.some((path) => url.endsWith(path));
};

// Response interceptor: differentiate session expiry (401) from MFA-required (403).
api.interceptors.response.use(
  (response) => {
    if (typeof window !== 'undefined' && shouldEmitSessionExtended(response.config?.url)) {
      window.dispatchEvent(new CustomEvent(SESSION_EXTENDED_EVENT, {
        detail: {
          url: response.config?.url,
          sessionTimeoutSeconds: response.data?.sessionTimeoutSeconds,
          sessionExpiresAtEpochMillis: response.data?.sessionExpiresAtEpochMillis,
        },
      }));
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const requestUrl = error.config?.url;
    const errorCode = error.response?.data?.error;

    // 401 LOGIN_REQUIRED → session expired or never authenticated.
    // Skip the redirect for the login endpoint itself (LoginPage shows the
    // failure inline) and for the session probe (AuthContext handles it).
    if (status === 401 && !isLoginFailurePath(requestUrl)) {
      window.location.href = `${APP_BASE}/login?reason=expired`;
      return Promise.reject(error);
    }

    // 403 MFA_REQUIRED → user is logged in but has not completed MFA.
    if (status === 403 && errorCode === 'MFA_REQUIRED') {
      // Don't redirect if already on the MFA page
      if (!window.location.pathname.endsWith('/mfa')) {
        window.location.href = `${APP_BASE}/mfa`;
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default api;
